import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, ScanLine, ChevronDown, ChevronRight, History, Camera, RefreshCw } from 'lucide-react';
import { QrScanner } from './QrScanner';
import { TeamControls } from './TeamControls';
import { CompanyShowcasePanel } from './CompanyShowcasePanel';
import { CompanyBroadcastPanel } from './CompanyBroadcastPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const tone = { confirmed: 'bg-emerald-50 text-emerald-700', locked: 'bg-amber-50 text-amber-700', forming: 'bg-blue-50 text-blue-700', cancelled: 'bg-gray-100 text-gray-500' };

const Stat = ({ label, value, id }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`teams-stat-${id}`}>
    <p className="text-xs text-[#666] uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-bold text-[#191919] mt-1">{value}</p>
  </div>
);

const CheckInBox = ({ eventId, headers, onDone }) => {
  const [code, setCode] = useState('');
  const [last, setLast] = useState(null);
  const [scanning, setScanning] = useState(false);
  const checkIn = async (value) => {
    try {
      const r = await axios.post(`${API}/api/event-teams/company/${eventId}/check-in`, { code: value.trim() }, { headers });
      setLast({ ok: true, msg: r.data.message, team: r.data.registration.team_name });
      toast.success(r.data.message); setCode(''); onDone();
    } catch (err) { const msg = err.response?.data?.detail || 'Check-in failed'; setLast({ ok: false, msg }); toast.error(msg); }
  };
  const submit = (e) => { e.preventDefault(); if (code.trim()) checkIn(code); };
  return (
    <form onSubmit={submit} className="bg-white border border-gray-200 rounded-xl p-4" data-testid="checkin-form">
      <p className="text-sm font-semibold text-[#191919] flex items-center gap-2 mb-2"><ScanLine size={16} /> Ticket check-in</p>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste ticket ID or scanned ticket URL"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]" data-testid="checkin-input" />
        <button className="px-4 py-2 bg-[#0a66c2] hover:bg-[#004182] text-white rounded-lg text-sm font-medium" data-testid="checkin-submit-btn">Check in</button>
        <button type="button" onClick={() => setScanning(true)} className="flex items-center gap-1.5 px-4 py-2 bg-[#191919] hover:bg-black text-white rounded-lg text-sm font-medium" data-testid="open-qr-scanner-btn"><Camera size={15} /> Scan QR</button>
      </div>
      {scanning && <QrScanner onScan={checkIn} onClose={() => setScanning(false)} />}
      {last && <p className={`text-xs mt-2 ${last.ok ? 'text-emerald-700' : 'text-red-600'}`} data-testid="checkin-result">{last.msg}{last.team ? ` · Team ${last.team}` : ''}</p>}
    </form>
  );
};

const TeamRow = ({ t, eventId, headers, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`company-team-row-${t.id}`}>
        <td className="px-4 py-3 font-medium text-[#191919] flex items-center gap-1.5">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{t.name}</td>
        <td className="px-4 py-3 text-[#666]">{t.leader_name || t.leader_email}</td>
        <td className="px-4 py-3 text-[#666]">{t.member_count}{t.pending_invites ? ` (+${t.pending_invites} invited)` : ''}</td>
        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${tone[t.status]}`}>{t.status}</span></td>
        <td className="px-4 py-3 text-[#666] capitalize">{(t.payment_status || '').replace('_', ' ')}</td>
        <td className="px-4 py-3 text-[#666] text-xs">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
      </tr>
      {open && <tr><td colSpan={6} className="p-0"><TeamControls eventId={eventId} team={t} headers={headers} onChange={onChange} /></td></tr>}
    </>
  );
};

const RefundsList = ({ eventId, headers }) => {
  const [rows, setRows] = useState([]);
  const load = () => axios.get(`${API}/api/event-teams/company/${eventId}/refunds`, { headers }).then((r) => setRows(r.data.refunds || [])).catch(() => {});
  useEffect(() => { load(); }, [eventId]);
  const sync = (rid) => axios.post(`${API}/api/event-teams/company/${eventId}/refunds/${rid}/sync`, {}, { headers }).then(load).catch(() => toast.error('Sync failed'));
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="refunds-list">
      <table className="w-full text-sm text-[#191919]">
        <thead><tr className="bg-gray-50 text-xs uppercase tracking-wider text-[#666]">{['Refund', 'Amount', 'Mode', 'Status', 'Note', 'Date', ''].map((h) => <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-[#666]">No refunds yet</td></tr> : rows.map((r) => (
            <tr key={r.id} className="border-t border-gray-100" data-testid={`refund-row-${r.refund_id}`}>
              <td className="px-4 py-2 font-mono text-xs">{r.refund_id}</td><td className="px-4 py-2">INR {r.amount}</td>
              <td className="px-4 py-2 capitalize">{r.mode}</td><td className="px-4 py-2 font-medium">{r.status}</td>
              <td className="px-4 py-2 text-[#666] text-xs">{r.note || '—'}</td><td className="px-4 py-2 text-xs text-[#666]">{new Date(r.created_at).toLocaleString()}</td>
              <td className="px-4 py-2">{r.mode === 'cashfree' && <button onClick={() => sync(r.refund_id)} title="Refresh status"><RefreshCw size={13} className="text-[#0a66c2]" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const CompanyTeamsPanel = (props) => {
  const [view, setView] = useState('teams');
  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-gray-200">
        {[['teams', 'Teams & check-in'], ['broadcast', 'Message all teams'], ['showcase', 'Winners showcase'], ['refunds', 'Refunds']].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`teams-view-${v}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${view === v ? 'border-[#0a66c2] text-[#0a66c2]' : 'border-transparent text-[#666]'}`}>{l}</button>
        ))}
      </div>
      {view === 'teams' && <TeamsView {...props} />}
      {view === 'showcase' && <CompanyShowcasePanel {...props} />}
      {view === 'refunds' && <RefundsList {...props} />}
      {view === 'broadcast' && <CompanyBroadcastPanel {...props} />}
    </div>
  );
};

const TeamsView = ({ eventId, headers }) => {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const load = async () => {
    try {
      const [t, a] = await Promise.all([
        axios.get(`${API}/api/event-teams/company/${eventId}/teams`, { headers }),
        axios.get(`${API}/api/event-teams/company/${eventId}/audit`, { headers }),
      ]);
      setData(t.data); setLogs(a.data.logs || []);
    } catch { toast.error('Failed to load teams'); }
  };
  useEffect(() => { load(); }, [eventId]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/api/event-teams/company/${eventId}/teams/export`, { headers, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(new Blob([res.data])); a.download = `event_${eventId}_teams.csv`; a.click();
    } catch { toast.error('Export failed'); }
  };

  if (!data) return <div className="py-10 text-center text-sm text-[#666]">Loading teams…</div>;
  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="company-teams-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat id="total" label="Teams" value={s.total_teams} />
        <Stat id="confirmed" label="Confirmed" value={s.confirmed} />
        <Stat id="pending" label="Forming / Locked" value={`${s.forming} / ${s.locked}`} />
        <Stat id="participants" label="Participants" value={s.participants} />
        <Stat id="checked-in" label="Checked in" value={s.checked_in} />
      </div>
      <CheckInBox eventId={eventId} headers={headers} onDone={load} />
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-[#191919]">Teams · revenue INR {Math.round(s.revenue).toLocaleString()}</p>
          <button onClick={exportCsv} className="flex items-center gap-1.5 text-sm text-[#0a66c2] font-medium" data-testid="export-teams-btn"><Download size={14} /> Export teams CSV</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-[#666]">
              {['Team', 'Leader', 'Members', 'Status', 'Payment', 'Created'].map((h) => <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.teams.length === 0
                ? <tr><td colSpan={6} className="px-4 py-10 text-center text-[#666]">No teams yet</td></tr>
                : data.teams.map((t) => <TeamRow key={t.id} t={t} eventId={eventId} headers={headers} onChange={load} />)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200">
        <button onClick={() => setShowAudit(!showAudit)} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#191919]" data-testid="toggle-audit-btn">
          <History size={15} /> Activity log ({logs.length}) {showAudit ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {showAudit && (
          <ul className="border-t border-gray-100 max-h-80 overflow-y-auto text-xs divide-y divide-gray-50" data-testid="audit-log-list">
            {logs.map((l) => (
              <li key={l.id} className="px-4 py-2 flex justify-between gap-3"><span className="font-medium text-[#191919]">{l.action.replace(/_/g, ' ')}</span><span className="text-[#666]">{new Date(l.at).toLocaleString()}</span></li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
